import { useMemo } from "react";
import { TAILWIND_PROPERTIES, TAILWIND_LOOKUP } from "@/lib/tailwind";
import { ClassType } from "@/lib/tailwind/properties";
import { ALL_MODIFIERS_SET } from "./useModifiers";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";

export enum PropertyType {
  WIDTH,
  MIN_WIDTH,
  MAX_WIDTH,
  HEIGHT,
  MIN_HEIGHT,
  MAX_HEIGHT,
  ROTATE,
  BORDER_RADIUS,
  BORDER_STYLE,
  BORDER_COLOR,
  BORDER_WIDTH,
  BORDER_TOP_COLOR,
  BORDER_TOP_WIDTH,
  BORDER_BOTTOM_COLOR,
  BORDER_BOTTOM_WIDTH,
  BORDER_LEFT_COLOR,
  BORDER_LEFT_WIDTH,
  BORDER_RIGHT_COLOR,
  BORDER_RIGHT_WIDTH,
  BORDER_TOP_LEFT_RADIUS,
  BORDER_TOP_RIGHT_RADIUS,
  BORDER_BOTTOM_RIGHT_RADIUS,
  BORDER_BOTTOM_LEFT_RADIUS,
  FONT_WEIGHT,
  FONT_SIZE,
  LINE_HEIGHT,
  LETTER_SPACING,
  PADDING,
  PADDING_VERTICAL,
  PADDING_HORIZONTAL,
  PADDING_TOP,
  PADDING_BOTTOM,
  PADDING_LEFT,
  PADDING_RIGHT,
  MARGIN,
  MARGIN_VERTICAL,
  MARGIN_HORIZONTAL,
  MARGIN_TOP,
  MARGIN_BOTTOM,
  MARGIN_LEFT,
  MARGIN_RIGHT,
  COLUMN_GAP,
  ROW_GAP,
  ANIMATION,
  BLUR,
  BOX_SHADOW,
  OPACITY,
  TEXT_COLOR,
  BACKGROUND_COLOR,
  JUSTIFY_CONTENT,
  ALIGN_ITEMS,
  ALIGN_CONTENT,
  FONT_FAMILY,
  FONT_STYLE,
  TEXT_DECORATION_LINE,
  LEFT,
  TOP,
  RIGHT,
  BOTTOM,
  POSITION,
  BACKGROUND_IMAGE,
  FROM_COLOR,
  TO_COLOR,
  VIA_COLOR,
  FROM_POSITION,
  TO_POSITION,
  VIA_POSITION,
}

// Module-scoped cache
const cache: { [key in PropertyType]?: ClassType[] } = {};

/**
 * These do not determine if a class is belonging to a particular property,
 * it simple extracts the value to display from the class if a property is already known
 */
export const EXTRACT_TAILWIND_DISPLAY_REGEX: { [key: string]: RegExp } = {
  [PropertyType.WIDTH]: /w-\[?([^\[\]]*)\]?$/,
  [PropertyType.MIN_WIDTH]: /min-w-\[?([^\[\]]*)\]?$/,
  [PropertyType.MAX_WIDTH]: /max-w-\[?([^\[\]]*)\]?$/,
  [PropertyType.HEIGHT]: /h-\[?([^\[\]]*)\]?$/,
  [PropertyType.MIN_HEIGHT]: /min-h-\[?([^\[\]]*)\]?$/,
  [PropertyType.MAX_HEIGHT]: /max-h-\[?([^\[\]]*)\]?$/,
  [PropertyType.ROTATE]: /rotate-\[?([^\[\]]*)\]?$/,
  [PropertyType.BORDER_RADIUS]: /rounded-\[?([^\[\]]*)\]?$/,
  [PropertyType.BORDER_TOP_LEFT_RADIUS]: /rounded-tl-\[?([^\[\]]*)\]?$/,
  [PropertyType.BORDER_TOP_RIGHT_RADIUS]: /rounded-tr-\[?([^\[\]]*)\]?$/,
  [PropertyType.BORDER_BOTTOM_RIGHT_RADIUS]: /rounded-br-\[?([^\[\]]*)\]?$/,
  [PropertyType.BORDER_BOTTOM_LEFT_RADIUS]: /rounded-bl-\[?([^\[\]]*)\]?$/,
  [PropertyType.FONT_WEIGHT]: /font-(?!sans|serif|mono)\[?([^\[\]]*)\]?$/,
  [PropertyType.FONT_FAMILY]: /font-(sans|serif|mono)$/,
  [PropertyType.FONT_STYLE]: /(italic)|(non-italic)/,
  [PropertyType.TEXT_DECORATION_LINE]:
    /(underline)|(overline)|(line-through)|(no-underline)/,
  [PropertyType.LINE_HEIGHT]: /leading-\[?([^\[\]]*)\]?$/,
  [PropertyType.LETTER_SPACING]: /tracking-\[?([^\[\]]*)\]?$/,
  [PropertyType.PADDING]: /p-\[?([^\[\]]*)\]?$/,
  [PropertyType.PADDING_VERTICAL]: /py-\[?([^\[\]]*)\]?$/,
  [PropertyType.PADDING_HORIZONTAL]: /px-\[?([^\[\]]*)\]?$/,
  [PropertyType.PADDING_TOP]: /(?:p|pt)-\[?([^\[\]]*)\]?$/,
  [PropertyType.PADDING_BOTTOM]: /(?:p|pb)-\[?([^\[\]]*)\]?$/,
  [PropertyType.PADDING_LEFT]: /(?:p|pl)-\[?([^\[\]]*)\]?$/,
  [PropertyType.PADDING_RIGHT]: /(?:p|pr)-\[?([^\[\]]*)\]?$/,
  [PropertyType.MARGIN]: /m-\[?([^\[\]]*)\]?$/,
  [PropertyType.MARGIN_VERTICAL]: /(?:m|my)-\[?([^\[\]]*)\]?$/,
  [PropertyType.MARGIN_HORIZONTAL]: /(?:m|mx)-\[?([^\[\]]*)\]?$/,
  [PropertyType.MARGIN_TOP]: /(?:m|mt)-\[?([^\[\]]*)\]?$/,
  [PropertyType.MARGIN_BOTTOM]: /(?:m|mb)-\[?([^\[\]]*)\]?$/,
  [PropertyType.MARGIN_LEFT]: /(?:m|ml)-\[?([^\[\]]*)\]?$/,
  [PropertyType.MARGIN_RIGHT]: /(?:m|mr)-\[?([^\[\]]*)\]?$/,
  [PropertyType.COLUMN_GAP]: /gap-x-\[?([^\[\]]*)\]?$/,
  [PropertyType.ROW_GAP]: /gap-y-\[?([^\[\]]*)\]?$/,
  [PropertyType.ANIMATION]: /animate-\[?([^\[\]]*)\]?$/,
  [PropertyType.BLUR]: /blur-\[?([^\[\]]*)\]?$/,
  [PropertyType.OPACITY]: /opacity-\[?([^\[\]]*)\]?$/,
  // Captures both short and long hex formats, as well as explicit text (e.g. bg-[blue])
  [PropertyType.BACKGROUND_COLOR]: /bg-\[?([^\[\]]*)\]?$/,
  [PropertyType.JUSTIFY_CONTENT]: /justify-\[?([^\[\]]*)\]?$/,
  [PropertyType.ALIGN_ITEMS]: /items-\[?([^\[\]]*)\]?$/,
  [PropertyType.ALIGN_CONTENT]: /content-\[?([^\[\]]*)\]?$/,
  [PropertyType.BORDER_STYLE]: /border-\[?([^\[\]]*)\]?$/,
  [PropertyType.BORDER_COLOR]: /border-\[?([^\[\]]*)\]?$/,
  [PropertyType.BORDER_WIDTH]: /border-\[?([^\[\]]*)\]?$/,
  [PropertyType.BORDER_TOP_COLOR]: /border(?:-t)?-\[?([^\[\]]*)\]?$/,
  [PropertyType.BORDER_TOP_WIDTH]: /border(?:-t)?-\[?([^\[\]]*)\]?$/,
  [PropertyType.BORDER_LEFT_COLOR]: /border(?:-r)?-\[?([^\[\]]*)\]?$/,
  [PropertyType.BORDER_LEFT_WIDTH]: /border(?:-l)?-\[?([^\[\]]*)\]?$/,
  [PropertyType.BORDER_RIGHT_COLOR]: /border(?:-r)?-\[?([^\[\]]*)\]?$/,
  [PropertyType.BORDER_RIGHT_WIDTH]: /border(?:-r)?-\[?([^\[\]]*)\]?$/,
  [PropertyType.BORDER_BOTTOM_COLOR]: /border(?:-b)?-\[?([^\[\]]*)\]?$/,
  [PropertyType.BORDER_BOTTOM_WIDTH]: /border(?:-b)?-\[?([^\[\]]*)\]?$/,

  [PropertyType.FONT_SIZE]: /text-\[?([^\[\]]*)\]?$/,
  [PropertyType.TEXT_COLOR]: /text-\[?([^\[\]]*)\]?$/,
  [PropertyType.BACKGROUND_IMAGE]: /bg-\[?([^\[\]]*)\]?$/,
};

// Note - for useUnknownTailwindClasses every filter must employ some sort of filter
// on the tailwind class itself, as that is the only known value when filtering those
const filterFunctions: { [key in PropertyType]: (prop: ClassType) => boolean } =
  {
    [PropertyType.WIDTH]: (prop) =>
      prop.css.startsWith("width:") || prop.tailwind.startsWith("w-"),
    [PropertyType.MIN_WIDTH]: (prop) =>
      prop.css.startsWith("min-width:") || prop.tailwind.startsWith("min-w-"),
    [PropertyType.MAX_WIDTH]: (prop) =>
      prop.css.startsWith("max-width:") || prop.tailwind.startsWith("max-w-"),
    [PropertyType.HEIGHT]: (prop) =>
      prop.css.startsWith("height:") || prop.tailwind.startsWith("h-"),
    [PropertyType.MIN_HEIGHT]: (prop) =>
      prop.css.startsWith("min-height:") || prop.tailwind.startsWith("min-h-"),
    [PropertyType.MAX_HEIGHT]: (prop) =>
      prop.css.startsWith("max-height:") || prop.tailwind.startsWith("max-h-"),
    [PropertyType.ROTATE]: (prop) => prop.tailwind.startsWith("rotate-"),
    [PropertyType.BORDER_RADIUS]: (prop) =>
      prop.css.startsWith("border-radius:") ||
      prop.tailwind.startsWith("rounded-"),
    [PropertyType.BORDER_TOP_LEFT_RADIUS]: (prop) =>
      prop.tailwind.startsWith("rounded-tl-"),
    [PropertyType.BORDER_TOP_RIGHT_RADIUS]: (prop) =>
      prop.tailwind.startsWith("rounded-tr-"),
    [PropertyType.BORDER_BOTTOM_RIGHT_RADIUS]: (prop) =>
      prop.tailwind.startsWith("rounded-br-"),
    [PropertyType.BORDER_BOTTOM_LEFT_RADIUS]: (prop) =>
      prop.tailwind.startsWith("rounded-bl-"),
    [PropertyType.BORDER_STYLE]: (prop) =>
      prop.css.startsWith("border-style:") &&
      prop.tailwind.startsWith("border-"),
    [PropertyType.BORDER_COLOR]: (prop) =>
      prop.css.startsWith("border-color:") &&
      prop.tailwind.startsWith("border-"),
    [PropertyType.BORDER_WIDTH]: (prop) =>
      prop.css.startsWith("border-width:") &&
      prop.tailwind.startsWith("border"),
    [PropertyType.BORDER_TOP_COLOR]: (prop) =>
      prop.css.startsWith("border-top-color:") &&
      prop.tailwind.startsWith("border-t-"),
    [PropertyType.BORDER_TOP_WIDTH]: (prop) =>
      prop.css.startsWith("border-top-width:") &&
      prop.tailwind.startsWith("border-t"),
    [PropertyType.BORDER_LEFT_COLOR]: (prop) =>
      prop.css.startsWith("border-left-color:") &&
      prop.tailwind.startsWith("border-l-"),
    [PropertyType.BORDER_LEFT_WIDTH]: (prop) =>
      prop.css.startsWith("border-left-width:") &&
      prop.tailwind.startsWith("border-l"),
    [PropertyType.BORDER_RIGHT_COLOR]: (prop) =>
      prop.css.startsWith("border-right-color:") &&
      prop.tailwind.startsWith("border-r-"),
    [PropertyType.BORDER_RIGHT_WIDTH]: (prop) =>
      prop.css.startsWith("border-right-width:") &&
      prop.tailwind.startsWith("border-r"),
    [PropertyType.BORDER_BOTTOM_COLOR]: (prop) =>
      prop.css.startsWith("border-bottom-color:") &&
      prop.tailwind.startsWith("border-b-"),
    [PropertyType.BORDER_BOTTOM_WIDTH]: (prop) =>
      prop.css.startsWith("border-bottom-width:") &&
      prop.tailwind.startsWith("border-b"),
    [PropertyType.FONT_WEIGHT]: (prop) =>
      prop.css.startsWith("font-weight:") ||
      (prop.tailwind.startsWith("font-") &&
        !["font-sans", "font-serif", "font-mono"].includes(prop.tailwind)),
    [PropertyType.FONT_FAMILY]: (prop) =>
      prop.css.startsWith("font-family:") ||
      ["font-sans", "font-serif", "font-mono"].includes(prop.tailwind),
    [PropertyType.FONT_STYLE]: (prop) =>
      prop.css.startsWith("font-style:") ||
      ["italic", "non-italic"].includes(prop.tailwind),
    [PropertyType.TEXT_DECORATION_LINE]: (prop) =>
      ["underline", "overline", "line-through", "no-underline"].includes(
        prop.tailwind
      ),
    [PropertyType.FONT_SIZE]: (prop) => prop.css.startsWith("font-size:"),
    [PropertyType.LINE_HEIGHT]: (prop) =>
      prop.css.startsWith("line-height:") ||
      prop.tailwind.startsWith("leading-"),
    [PropertyType.LETTER_SPACING]: (prop) =>
      prop.css.startsWith("letter-spacing:") ||
      prop.tailwind.startsWith("tracking-"),
    [PropertyType.PADDING]: (prop) =>
      prop.css.startsWith("padding:") || prop.tailwind.startsWith("p-"),
    [PropertyType.PADDING_VERTICAL]: (prop) => prop.tailwind.startsWith("py-"),
    [PropertyType.PADDING_HORIZONTAL]: (prop) =>
      prop.tailwind.startsWith("px-"),
    [PropertyType.PADDING_TOP]: (prop) => prop.tailwind.startsWith("pt-"),
    [PropertyType.PADDING_BOTTOM]: (prop) => prop.tailwind.startsWith("pb-"),
    [PropertyType.PADDING_LEFT]: (prop) => prop.tailwind.startsWith("pl-"),
    [PropertyType.PADDING_RIGHT]: (prop) => prop.tailwind.startsWith("pr-"),
    [PropertyType.MARGIN]: (prop) =>
      prop.css.startsWith("margin:") || prop.tailwind.startsWith("m-"),
    [PropertyType.MARGIN_VERTICAL]: (prop) => prop.tailwind.startsWith("my-"),
    [PropertyType.MARGIN_HORIZONTAL]: (prop) => prop.tailwind.startsWith("mx-"),
    [PropertyType.MARGIN_TOP]: (prop) => prop.tailwind.startsWith("mt-"),
    [PropertyType.MARGIN_BOTTOM]: (prop) => prop.tailwind.startsWith("mb-"),
    [PropertyType.MARGIN_LEFT]: (prop) => prop.tailwind.startsWith("ml-"),
    [PropertyType.MARGIN_RIGHT]: (prop) => prop.tailwind.startsWith("mr-"),
    [PropertyType.COLUMN_GAP]: (prop) =>
      prop.css.startsWith("column-gap:") || prop.tailwind.startsWith("gap-x-"),
    [PropertyType.ROW_GAP]: (prop) =>
      prop.css.startsWith("row-gap:") || prop.tailwind.startsWith("gap-y-"),
    [PropertyType.ANIMATION]: (prop) => prop.tailwind.startsWith("animate-"),
    [PropertyType.BLUR]: (prop) => prop.tailwind.startsWith("blur"),
    [PropertyType.OPACITY]: (props) =>
      props.css.startsWith("opacity:") || props.tailwind.startsWith("opacity-"),
    [PropertyType.BACKGROUND_COLOR]: (prop) =>
      prop.css.startsWith("background-color:") ||
      TAILWIND_LOOKUP[prop.tailwind]?.startsWith?.("background-color:") ||
      Boolean(
        prop.tailwind.match(
          /bg-\[#([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{4}|[0-9a-fA-F]{8})\]/
        )
      ) || // Captures both short and long hex formats
      Boolean(prop.tailwind.match(/bg-\[[A-Za-z]+\]/)), // text values (e.g. bg-[blue])

    [PropertyType.JUSTIFY_CONTENT]: (prop) =>
      prop.css.startsWith("justify-content:") ||
      TAILWIND_LOOKUP[prop.tailwind]?.startsWith?.("justify-content:"),

    [PropertyType.ALIGN_ITEMS]: (prop) =>
      prop.css.startsWith("align-items:") ||
      TAILWIND_LOOKUP[prop.tailwind]?.startsWith?.("align-items:"),

    [PropertyType.ALIGN_CONTENT]: (prop) =>
      prop.css.startsWith("align-content:") ||
      TAILWIND_LOOKUP[prop.tailwind]?.startsWith?.("align-content:"),

    // Note: These will not be filterable out of the unknown classes because they require css to be passed in
    // TODO: If we can convert the unknown classes to css as well we can tell if they are truly unknown
    [PropertyType.BOX_SHADOW]: (prop) =>
      (prop.css.startsWith("box-shadow:") ||
        TAILWIND_LOOKUP[prop.tailwind]?.startsWith?.("box-shadow:")) &&
      prop.tailwind.startsWith("shadow"),

    [PropertyType.TEXT_COLOR]: (prop) =>
      prop.css.startsWith("color:") && prop.tailwind.startsWith("text-"),
    [PropertyType.LEFT]: (prop) =>
      // @ts-expect-error TS2322
      (!prop.css || (prop.css && prop.css.startsWith("left:"))) &&
      prop.tailwind.startsWith("left-"),
    [PropertyType.TOP]: (prop) =>
      // @ts-expect-error TS2322
      (!prop.css || (prop.css && prop.css.startsWith("top:"))) &&
      prop.tailwind.startsWith("top-"),
    [PropertyType.RIGHT]: (prop) =>
      // @ts-expect-error TS2322
      (!prop.css || (prop.css && prop.css.startsWith("right:"))) &&
      prop.tailwind.startsWith("right-"),
    [PropertyType.BOTTOM]: (prop) =>
      // @ts-expect-error TS2322
      (!prop.css || (prop.css && prop.css.startsWith("bottom:"))) &&
      prop.tailwind.startsWith("bottom-"),
    [PropertyType.POSITION]: (prop) =>
      // @ts-expect-error TS2322
      (!prop.css || (prop.css && prop.css.startsWith("position:"))) &&
      ["absolute", "relative", "fixed", "sticky", "static"].includes(
        prop.tailwind
      ),
    [PropertyType.BACKGROUND_IMAGE]: (prop) =>
      (!prop.css || prop.css.startsWith("background-image:")) &&
      ([
        "bg-none",
        "bg-gradient-to-t",
        "bg-gradient-to-tr",
        "bg-gradient-to-r",
        "bg-gradient-to-br",
        "bg-gradient-to-b",
        "bg-gradient-to-bl",
        "bg-gradient-to-l",
        "bg-gradient-to-tl",
      ].includes(prop.tailwind) ||
        prop.tailwind.startsWith("bg-[url(")),
    [PropertyType.FROM_COLOR]: (prop) =>
      prop.tailwind.startsWith("from-") &&
      (!prop.css || !prop.css.startsWith("--tw-gradient-from-position:")),
    [PropertyType.TO_COLOR]: (prop) =>
      prop.tailwind.startsWith("to-") &&
      (!prop.css || !prop.css.startsWith("--tw-gradient-to-position:")),
    [PropertyType.VIA_COLOR]: (prop) =>
      prop.tailwind.startsWith("via-") &&
      (!prop.css || !prop.css.startsWith("--tw-gradient-via-position:")),
    [PropertyType.FROM_POSITION]: (prop) =>
      prop.tailwind.startsWith("from-") &&
      prop.css.startsWith("--tw-gradient-from-position:"),
    [PropertyType.TO_POSITION]: (prop) =>
      prop.tailwind.startsWith("to-") &&
      prop.css.startsWith("--tw-gradient-to-position:"),
    [PropertyType.VIA_POSITION]: (prop) =>
      prop.tailwind.startsWith("via-") &&
      prop.css.startsWith("--tw-gradient-via-position:"),
  };

const filterFunctionList = Object.values(filterFunctions);

export const useTailwindProperties = (type: PropertyType): ClassType[] => {
  const conditions = useCodebaseStore((state) => state.conditions);
  const tailwindInstalled = conditions && conditions["tailwindInstalled"];

  return useMemo(() => {
    const localProperties = TAILWIND_PROPERTIES;
    let allProperties = [...localProperties];

    if (
      tailwindInstalled?.met &&
      !tailwindInstalled.errors &&
      tailwindInstalled.data?.properties
    ) {
      const serverProperties = tailwindInstalled.data.properties;
      allProperties = [...localProperties, ...serverProperties];
    }

    const filteredProperties = allProperties.filter(filterFunctions[type]);
    return filteredProperties;
  }, [type, tailwindInstalled]);
};

export const useUnknownTailwindClasses = (classList: string[]): string[] => {
  const conditions = useCodebaseStore((state) => state.conditions);
  const tailwindInstalled = conditions && conditions["tailwindInstalled"];

  return useMemo(() => {
    if (!classList) {
      return [];
    }

    return classList.filter((className: string) => {
      if (
        className.startsWith("tempo-") ||
        className.startsWith("arb89-") ||
        className.startsWith("temp-")
      ) {
        return false;
      }

      // Note, all text properties are supported in the styling panel,
      // but it's difficult to filter for which property exactly is being used.
      // So filter here instead of adding to the filter functions
      if (className.startsWith("text-")) {
        return false;
      }

      // Similarly, all border properties are supported
      if (className.startsWith("border-")) {
        return false;
      }

      // All flex properties are supported
      if (className.startsWith("flex")) {
        return false;
      }

      // Strip modifier name from class
      ALL_MODIFIERS_SET.forEach((modifier) => {
        if (className.startsWith(`${modifier}:`)) {
          // Replace from start of string
          className = className.replace(`${modifier}:`, "");
        }
      });

      // If there are custom properties, directly check if the class is in the list
      if (
        tailwindInstalled?.met &&
        !tailwindInstalled.errors &&
        tailwindInstalled.data?.properties
      ) {
        if (
          tailwindInstalled.data.properties.find(
            (prop: ClassType) => prop.tailwind === className
          )
        ) {
          return false;
        }
      }

      return !filterFunctionList.find((filterFn: any) => {
        return filterFn({ tailwind: className, css: "" });
      });
    });
  }, [classList, tailwindInstalled]);
};
